// apps/provider/src/services/api/waitlist.js
// Waitlist management: CRUD, matching, conversion to appointments.
//
// Methods: getWaitlistEntries, getAllWaitlistEntries, getWaitlistEntry,
//          createWaitlistEntry, updateWaitlistEntry, removeWaitlistEntry,
//          convertWaitlistEntry
//
// Response types: @prism-ehr/shared → WaitlistEntryResponse,
//                 WaitlistConvertResult

/**
 * @returns {Object} Waitlist domain methods (bound to `this` of the composed apiService)
 */
export function createWaitlistMethods() {
  return {

    /**
     * List waitlist entries for the organization.
     *
     * Xano: GET /waitlist
     *
     * @param {Object} filters - { organization_id?, location_id?, provider_ids?, status?, priority?, page?, per_page? }
     * @returns {Promise<import('@prism-ehr/shared').WaitlistEntryResponse[]>}
     */
    getWaitlistEntries(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) params.append(key, val);
      });
      return this.request(`/waitlist?${params.toString()}`, { method: 'GET' });
    },

    /**
     * List ALL waitlist entries for the organization, following pagination.
     * Used by consumers that need the complete set (calendar slot matching,
     * the waitlist list page) — a single page silently truncates.
     *
     * Xano: GET /waitlist (page loop)
     *
     * @param {Object} filters - { status?, priority? }
     * @returns {Promise<import('@prism-ehr/shared').WaitlistEntryResponse[]>}
     */
    async getAllWaitlistEntries(filters = {}) {
      const MAX_PAGES = 10;
      const all = [];
      let page = 1;
      for (let i = 0; i < MAX_PAGES; i++) {
        const data = await this.getWaitlistEntries({ ...filters, page, per_page: 100 });
        const items = Array.isArray(data) ? data : data?.items || [];
        all.push(...items);
        if (Array.isArray(data) || !data?.nextPage) break;
        page = data.nextPage;
      }
      return all;
    },

    /**
     * Get a single waitlist entry by ID.
     *
     * Xano: GET /waitlist/{id}
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').WaitlistEntryResponse>}
     */
    getWaitlistEntry(id) {
      return this.request(`/waitlist/${id}`, { method: 'GET' });
    },

    /**
     * Create a new waitlist entry.
     *
     * Xano: POST /waitlist
     *
     * @param {Object} data - { organization_id?, location_id?, patient_id?, contact_info, provider_preference, provider_ids?, appointment_type_id?, duration_minutes?, time_preferences?, earliest_date?, latest_date?, priority?, notes? }
     * @returns {Promise<import('@prism-ehr/shared').WaitlistEntryResponse>}
     */
    createWaitlistEntry(data) {
      return this.request('/waitlist', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an existing waitlist entry.
     *
     * Xano: PATCH /waitlist/{id}
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').WaitlistEntryResponse>}
     */
    updateWaitlistEntry(id, data) {
      return this.request(`/waitlist/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Soft-delete a waitlist entry (sets status to 'removed').
     *
     * Xano: DELETE /waitlist/{id}
     *
     * @param {string} id
     * @returns {Promise<void>}
     */
    removeWaitlistEntry(id) {
      return this.request(`/waitlist/${id}`, { method: 'DELETE' });
    },

    /**
     * Convert a waitlist entry to a confirmed appointment.
     *
     * Xano: POST /waitlist-convert/{id}
     *
     * @param {string} id - Waitlist entry ID
     * @param {Object} data - { patient_id?, appointment_date, start_time, end_time, provider_id, appointment_type_id?, duration_minutes?, is_telehealth?, location_id?, send_confirmation?, send_email?, send_sms? }
     * @returns {Promise<import('@prism-ehr/shared').WaitlistConvertResult>}
     */
    convertWaitlistEntry(id, data) {
      return this.request(`/waitlist-convert/${id}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };
}
